import DefaultShader from "_src/shaders/default/default_shader";
import PBRShader from "_src/shaders/pbr/pbr";
import appState from "_src/app_state";
import loader from "_src/loader";
import output from "_src/engine/output";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import {pixelBufferType, textureMinFilter, textureMagFilter, blendEquation, cubeMapTarget} from "_src/engine/constants";
import {colorType} from "_src/constants";
import LineShader from "_src/shaders/line/line";

let _pbrResources = null;

class Material {
  constructor(shader, properties) {
    this.shader = shader;
    this.properties = properties;
    this.textures = [];
  }
}

export class ColorTextureMaterial extends Material {
  constructor(properties) {
    const shader = new DefaultShader({
      backFaceCulling: false,
      writeObjectID: true,
      drawDepth: appState.getState(appState.GS_DRAW_DEPTH),
      monochromaticColor: true,
    });
    const backfaceColor = appState.getStateValue("backFaceColor");
    shader.setBackFaceColor(backfaceColor.r, backfaceColor.g, backfaceColor.b);

    super(shader, properties);
  }
}

export class MatCapMaterial extends Material {
  constructor(properties) {
    const shader = new DefaultShader({
      backFaceCulling: false,
      writeObjectID: true,
      drawDepth: appState.getState(appState.GS_DRAW_DEPTH),
      lighting: false,
      colorMode: colorType.MATCAP,
    });
    const backfaceColor = appState.getStateValue("backFaceColor");
    shader.setBackFaceColor(backfaceColor.r, backfaceColor.g, backfaceColor.b);

    super(shader, properties);

    this.initPromise = this._loadTexture();
  }

  async _loadTexture() {
    try {
      const image = await loader.load(this.properties.matCapPath, loader.IMAGE);

      if (!image) {
        output.error(output.DEVELOPERS_ERROR, "Couldn't load matcap texture.");
        return;
      }

      const minFilter = textureMinFilter.NEAREST;
      const magFilter = textureMagFilter.NEAREST;

      const matCapTexture = bufferFactory.create(pixelBufferType.T_RGBA, {
        width: image.width, height: image.height,
        minFilter, magFilter, immutable: true, flipY: true
      });
      matCapTexture.initMipMapLevelImage(0, image);
      this.textures[0] = matCapTexture;
    } catch (error) {
      if (!loader.isLegalError(error)) {
        output.error(output.DEVELOPERS_ERROR, "Couldn't load matcap texture. " + error);
      }
    }
  }
}

export class PBRMaterial extends Material {
  constructor(properties) {
    const shader = new PBRShader({backFaceCulling: false});
    super(shader, properties);
    const backfaceColor = appState.getStateValue("backFaceColor");
    shader.setBackFaceColor(backfaceColor.r, backfaceColor.g, backfaceColor.b);

    this.resources = null;
    this.initPromise = _initPbrResources().then(res => {
      this.resources = res;
    })
  }
}

export class SurfelsMaterial extends Material {
  constructor(properties) {
    const shader = new DefaultShader({
      backFaceCulling: false,
      writeObjectID: true,
      surfelsMode: true,
      surfelsSizeConstraint: false,     //bound max surfel's screen size (model will break into quads on zoom)
      drawDepth: appState.getState(appState.GS_DRAW_DEPTH),
      monochromaticColor: true,
    });

    const backfaceColor = appState.getStateValue("backFaceColor");
    shader.setBackFaceColor(backfaceColor.r, backfaceColor.g, backfaceColor.b);

    super(shader, properties);
  }
}

export class CADMaterial extends Material {
  constructor(properties) {
    const shader = new DefaultShader({
      backFaceCulling: false,
      writeObjectID: true,
      backFaceMode: appState.BM_FRONTFACE,
      fresnelAlpha: true,
      drawDepth: appState.getState(appState.GS_DRAW_DEPTH),
      monochromaticColor: true,
    });

    super(shader, properties);
  }
}

export class CADEdgesMaterial extends Material {
  constructor(properties) {
    const EDGE_WIDTH = 2;

    const shader = new LineShader({
      antialiasing: true,
      lineWidth : EDGE_WIDTH,
      transparent : true,
      backFaceCulling: true,
      screenSpace : false,
      depthTest: true,
    });
    shader.setBlendEquation(blendEquation.FUNC_ADD, blendEquation.MAX);
    shader.enablePolygonOffset();
    shader.setPolygonOffset(-3,-3);

    super(shader, properties);
  }
}

export function cleanupMaterialsResources() {
  _pbrResources = null;
}

function _initPbrResources() {
  if (_pbrResources === null) {
    _pbrResources = {
      initPromise: _loadPbrTextures(),

      irradiance: null,
      prefilter: null,
      brdf: null,
    };
  }

  return _pbrResources.initPromise;
}

async function _loadPbrTextures() {
  const irradianceDescriptors = [
    {path: require("_assets/envMaps/irradiance_posx.png"), target: cubeMapTarget.POSITIVE_X, image: null},
    {path: require("_assets/envMaps/irradiance_negx.png"), target: cubeMapTarget.NEGATIVE_X, image: null},
    {path: require("_assets/envMaps/irradiance_posy.png"), target: cubeMapTarget.POSITIVE_Y, image: null},
    {path: require("_assets/envMaps/irradiance_negy.png"), target: cubeMapTarget.NEGATIVE_Y, image: null},
    {path: require("_assets/envMaps/irradiance_posz.png"), target: cubeMapTarget.POSITIVE_Z, image: null},
    {path: require("_assets/envMaps/irradiance_negz.png"), target: cubeMapTarget.NEGATIVE_Z, image: null},
  ];
  const irradiancePromises = [];
  for (const desc of irradianceDescriptors) {
    const promise = loader.load(desc.path, loader.IMAGE).then(image => {
      desc.image = image;
    }).catch(e => {
      output.warn("can't load irradiance map part: " + e);
    });
    irradiancePromises.push(promise);
  }
  await Promise.allSettled(irradiancePromises);
  const anyIrradianceImageDesc = irradianceDescriptors.find(desc => desc.image);
  const irradianceTexture = bufferFactory.create(pixelBufferType.CM_RGBA,
      {
        width: anyIrradianceImageDesc ? anyIrradianceImageDesc.image.width : 1,
        height: anyIrradianceImageDesc ? anyIrradianceImageDesc.image.height : 1,
      });
  for (const desc of irradianceDescriptors) {
    if (!desc.image) continue;

    irradianceTexture.initMipMapLevelImage(0, desc.image, desc.target);
  }
  _pbrResources.irradiance = irradianceTexture;

  const MIPS_COUNT = 10;
  const prefilterDescriptors = [
    {path: require("_assets/envMaps/radiance_posx_0.png"), target: cubeMapTarget.POSITIVE_X, level: 0, image: null},
    {path: require("_assets/envMaps/radiance_posx_1.png"), target: cubeMapTarget.POSITIVE_X, level: 1, image: null},
    {path: require("_assets/envMaps/radiance_posx_2.png"), target: cubeMapTarget.POSITIVE_X, level: 2, image: null},
    {path: require("_assets/envMaps/radiance_posx_3.png"), target: cubeMapTarget.POSITIVE_X, level: 3, image: null},
    {path: require("_assets/envMaps/radiance_posx_4.png"), target: cubeMapTarget.POSITIVE_X, level: 4, image: null},
    {path: require("_assets/envMaps/radiance_posx_5.png"), target: cubeMapTarget.POSITIVE_X, level: 5, image: null},
    {path: require("_assets/envMaps/radiance_posx_6.png"), target: cubeMapTarget.POSITIVE_X, level: 6, image: null},
    {path: require("_assets/envMaps/radiance_posx_7.png"), target: cubeMapTarget.POSITIVE_X, level: 7, image: null},
    {path: require("_assets/envMaps/radiance_posx_8.png"), target: cubeMapTarget.POSITIVE_X, level: 8, image: null},
    {path: require("_assets/envMaps/radiance_posx_9.png"), target: cubeMapTarget.POSITIVE_X, level: 9, image: null},

    {path: require("_assets/envMaps/radiance_negx_0.png"), target: cubeMapTarget.NEGATIVE_X, level: 0, image: null},
    {path: require("_assets/envMaps/radiance_negx_1.png"), target: cubeMapTarget.NEGATIVE_X, level: 1, image: null},
    {path: require("_assets/envMaps/radiance_negx_2.png"), target: cubeMapTarget.NEGATIVE_X, level: 2, image: null},
    {path: require("_assets/envMaps/radiance_negx_3.png"), target: cubeMapTarget.NEGATIVE_X, level: 3, image: null},
    {path: require("_assets/envMaps/radiance_negx_4.png"), target: cubeMapTarget.NEGATIVE_X, level: 4, image: null},
    {path: require("_assets/envMaps/radiance_negx_5.png"), target: cubeMapTarget.NEGATIVE_X, level: 5, image: null},
    {path: require("_assets/envMaps/radiance_negx_6.png"), target: cubeMapTarget.NEGATIVE_X, level: 6, image: null},
    {path: require("_assets/envMaps/radiance_negx_7.png"), target: cubeMapTarget.NEGATIVE_X, level: 7, image: null},
    {path: require("_assets/envMaps/radiance_negx_8.png"), target: cubeMapTarget.NEGATIVE_X, level: 8, image: null},
    {path: require("_assets/envMaps/radiance_negx_9.png"), target: cubeMapTarget.NEGATIVE_X, level: 9, image: null},

    {path: require("_assets/envMaps/radiance_posy_0.png"), target: cubeMapTarget.POSITIVE_Y, level: 0, image: null},
    {path: require("_assets/envMaps/radiance_posy_1.png"), target: cubeMapTarget.POSITIVE_Y, level: 1, image: null},
    {path: require("_assets/envMaps/radiance_posy_2.png"), target: cubeMapTarget.POSITIVE_Y, level: 2, image: null},
    {path: require("_assets/envMaps/radiance_posy_3.png"), target: cubeMapTarget.POSITIVE_Y, level: 3, image: null},
    {path: require("_assets/envMaps/radiance_posy_4.png"), target: cubeMapTarget.POSITIVE_Y, level: 4, image: null},
    {path: require("_assets/envMaps/radiance_posy_5.png"), target: cubeMapTarget.POSITIVE_Y, level: 5, image: null},
    {path: require("_assets/envMaps/radiance_posy_6.png"), target: cubeMapTarget.POSITIVE_Y, level: 6, image: null},
    {path: require("_assets/envMaps/radiance_posy_7.png"), target: cubeMapTarget.POSITIVE_Y, level: 7, image: null},
    {path: require("_assets/envMaps/radiance_posy_8.png"), target: cubeMapTarget.POSITIVE_Y, level: 8, image: null},
    {path: require("_assets/envMaps/radiance_posy_9.png"), target: cubeMapTarget.POSITIVE_Y, level: 9, image: null},

    {path: require("_assets/envMaps/radiance_negy_0.png"), target: cubeMapTarget.NEGATIVE_Y, level: 0, image: null},
    {path: require("_assets/envMaps/radiance_negy_1.png"), target: cubeMapTarget.NEGATIVE_Y, level: 1, image: null},
    {path: require("_assets/envMaps/radiance_negy_2.png"), target: cubeMapTarget.NEGATIVE_Y, level: 2, image: null},
    {path: require("_assets/envMaps/radiance_negy_3.png"), target: cubeMapTarget.NEGATIVE_Y, level: 3, image: null},
    {path: require("_assets/envMaps/radiance_negy_4.png"), target: cubeMapTarget.NEGATIVE_Y, level: 4, image: null},
    {path: require("_assets/envMaps/radiance_negy_5.png"), target: cubeMapTarget.NEGATIVE_Y, level: 5, image: null},
    {path: require("_assets/envMaps/radiance_negy_6.png"), target: cubeMapTarget.NEGATIVE_Y, level: 6, image: null},
    {path: require("_assets/envMaps/radiance_negy_7.png"), target: cubeMapTarget.NEGATIVE_Y, level: 7, image: null},
    {path: require("_assets/envMaps/radiance_negy_8.png"), target: cubeMapTarget.NEGATIVE_Y, level: 8, image: null},
    {path: require("_assets/envMaps/radiance_negy_9.png"), target: cubeMapTarget.NEGATIVE_Y, level: 9, image: null},

    {path: require("_assets/envMaps/radiance_posz_0.png"), target: cubeMapTarget.POSITIVE_Z, level: 0, image: null},
    {path: require("_assets/envMaps/radiance_posz_1.png"), target: cubeMapTarget.POSITIVE_Z, level: 1, image: null},
    {path: require("_assets/envMaps/radiance_posz_2.png"), target: cubeMapTarget.POSITIVE_Z, level: 2, image: null},
    {path: require("_assets/envMaps/radiance_posz_3.png"), target: cubeMapTarget.POSITIVE_Z, level: 3, image: null},
    {path: require("_assets/envMaps/radiance_posz_4.png"), target: cubeMapTarget.POSITIVE_Z, level: 4, image: null},
    {path: require("_assets/envMaps/radiance_posz_5.png"), target: cubeMapTarget.POSITIVE_Z, level: 5, image: null},
    {path: require("_assets/envMaps/radiance_posz_6.png"), target: cubeMapTarget.POSITIVE_Z, level: 6, image: null},
    {path: require("_assets/envMaps/radiance_posz_7.png"), target: cubeMapTarget.POSITIVE_Z, level: 7, image: null},
    {path: require("_assets/envMaps/radiance_posz_8.png"), target: cubeMapTarget.POSITIVE_Z, level: 8, image: null},
    {path: require("_assets/envMaps/radiance_posz_9.png"), target: cubeMapTarget.POSITIVE_Z, level: 9, image: null},

    {path: require("_assets/envMaps/radiance_negz_0.png"), target: cubeMapTarget.NEGATIVE_Z, level: 0, image: null},
    {path: require("_assets/envMaps/radiance_negz_1.png"), target: cubeMapTarget.NEGATIVE_Z, level: 1, image: null},
    {path: require("_assets/envMaps/radiance_negz_2.png"), target: cubeMapTarget.NEGATIVE_Z, level: 2, image: null},
    {path: require("_assets/envMaps/radiance_negz_3.png"), target: cubeMapTarget.NEGATIVE_Z, level: 3, image: null},
    {path: require("_assets/envMaps/radiance_negz_4.png"), target: cubeMapTarget.NEGATIVE_Z, level: 4, image: null},
    {path: require("_assets/envMaps/radiance_negz_5.png"), target: cubeMapTarget.NEGATIVE_Z, level: 5, image: null},
    {path: require("_assets/envMaps/radiance_negz_6.png"), target: cubeMapTarget.NEGATIVE_Z, level: 6, image: null},
    {path: require("_assets/envMaps/radiance_negz_7.png"), target: cubeMapTarget.NEGATIVE_Z, level: 7, image: null},
    {path: require("_assets/envMaps/radiance_negz_8.png"), target: cubeMapTarget.NEGATIVE_Z, level: 8, image: null},
    {path: require("_assets/envMaps/radiance_negz_9.png"), target: cubeMapTarget.NEGATIVE_Z, level: 9, image: null},
  ];
  const prefilterPromises = [];
  for (const desc of prefilterDescriptors) {
    const promise = loader.load(desc.path, loader.IMAGE).then(image => {
      desc.image = image;
    }).catch(e => {
      output.warn("can't load prefilter map part: " + e);
    });
    prefilterPromises.push(promise);
  }
  await Promise.allSettled(prefilterPromises);
  const anyPrefilterImageDesc = prefilterDescriptors.find(desc => desc.level === 0 && desc.image);
  const prefilterTexture = bufferFactory.create(pixelBufferType.CM_RGBA,
      {
        width: anyPrefilterImageDesc ? anyPrefilterImageDesc.image.width : 1,
        height: anyPrefilterImageDesc ? anyPrefilterImageDesc.image.height : 1,
        levels: MIPS_COUNT,
        minFilter: textureMinFilter.LINEAR_MIPMAP_LINEAR,
      });
  for (const desc of prefilterDescriptors) {
    if (!desc.image) continue;

    prefilterTexture.initMipMapLevelImage(desc.level, desc.image, desc.target);
  }
  _pbrResources.prefilter = prefilterTexture;

  const brdfLUTPath = require("_assets/envMaps/brdf.png");
  const brdfLUTImage = await loader.load(brdfLUTPath, loader.IMAGE);
  const brdfLUTTexture = bufferFactory.create(pixelBufferType.T_RGBA,
      {
        width: brdfLUTImage ? brdfLUTImage.width : 1,
        height: brdfLUTImage ? brdfLUTImage.height : 1,
        immutable: true,
      });
  if (brdfLUTImage) {
    brdfLUTTexture.initMipMapLevelImage(0, brdfLUTImage);
  }
  _pbrResources.brdf = brdfLUTTexture;

  return _pbrResources;
}
